import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useTranslate } from 'react-polyglot';
import { useAuth } from '../../../Auth';
import { useAxios } from '../../../AxiosHandler';

import { Col, Container, Row } from 'react-bootstrap';

import styles from './SelectedSicknessReport.module.scss';
import Card from '../../Shared/Card/Card';
import { general } from '../../../general';
import HealthStatusRows from '../../Shared/HealthStatusRows/HealthStatusRows';

function SelectedSicknessReport(props: SelectedSicknessReportProps) {
    const t = useTranslate();
    const navigate = useNavigate();
    const auth = useAuth();
    const axiosHandler = useAxios();
    const axios = axiosHandler.axios;
    const params = useParams();
    const location = useLocation();

    // Listesidas query-streng (fanen, eller åpen seksjon på /admin) følger med hit
    // i history state, slik at knappene under leverer brukeren tilbake til det de
    // kom fra og ikke til standardvisningen. Åpnet man detaljsiden via en direkte
    // lenke finnes ingen state, og vi lander på listesida uendret.
    const listUrl = "/sykdomsrapporter" + ((location.state as any)?.listSearch ?? "");

    const [selectedReg, setSelectedReg] = useState<any | undefined>();
    const [searchTerm, setSearchTerm] = useState<string>("");
    const [searchResults, setSearchResults] = useState<any[]>([]);
    const [isSearching, setIsSearching] = useState<boolean>(false);

    function getBreedingDog() {
        axios.single({
            method: 'get',
            url: "/api/schema/" + params.schemakey + "/" + params.breedregid + "/" + auth.selectedBreed,
            responseType: 'json',
            headers: { 'x-access-token': auth.user['token'], "selected-country": auth.selectedCountry || "all" }
        }).then((res: any) => {
            setSelectedReg(res.data);
        }).catch((err: any) => {
            console.log(err);
        });
    }

    function acceptBreeding() {
        axios.single({
            method: 'post',
            url: "/api/schema" + "/accept/" + params.schemakey + "/" + params.breedregid + "/" + auth.selectedBreed,
            responseType: 'json',
            headers: { 'x-access-token': auth.user['token'], "selected-country": auth.selectedCountry || "all" }
        }).then((res) => {
            navigate(listUrl);
        }).catch((err: any) => {
            console.log(err);
        });
    }

    function rejectBreeding() {
        axios.single({
            method: 'post',
            url: "/api/schema" + "/reject/" + params.schemakey + "/" + params.breedregid + "/" + auth.selectedBreed,
            responseType: 'json',
            headers: { 'x-access-token': auth.user['token'], "selected-country": auth.selectedCountry || "all" }
        }).then((res) => {
            navigate(listUrl);
        }).catch((err: any) => {
            console.log(err);
        });
    }

    function searchDog() {
        if (!searchTerm || searchTerm.trim() === "") {
            return;
        }
        
        setIsSearching(true);
        axios.single({
            method: 'post',
            url: "/api/dogs/searchdogs",
            data: {
                breedid: auth.selectedBreed,
                search: searchTerm
            },
            responseType: 'json',
            headers: { 'x-access-token': auth.user['token'], "selected-country": auth.selectedCountry || "all" }
        }).then((res: any) => {
            setSearchResults(res.data);
        }).catch((err: any) => {
            console.log(err);
        }).finally(() => {
            setIsSearching(false);
        });
    }

    function selectDog(dog: any) {
        axios.single({
            method: 'post',
            url: "/api/schema/updateDogMatch/" + params.schemakey + "/" + params.breedregid + "/" + auth.selectedBreed,
            data: {
                animalId: dog.ANIMAL_ID,
                regnumber: dog.REGNO
            },
            responseType: 'json',
            headers: { 'x-access-token': auth.user['token'], "selected-country": auth.selectedCountry || "all" }
        }).then((res: any) => {
            setSelectedReg(res.data);
            setSearchResults([]);
            setSearchTerm("");
        }).catch((err: any) => {
            console.log(err);
        });
    }

    function removeDogMatch() {
        axios.single({
            method: 'post',
            url: "/api/schema/removeDogMatch/" + params.schemakey + "/" + params.breedregid + "/" + auth.selectedBreed,
            responseType: 'json',
            headers: { 'x-access-token': auth.user['token'], "selected-country": auth.selectedCountry || "all" }
        }).then((res: any) => {
            setSelectedReg(res.data);
        }).catch((err: any) => {
            console.log(err);
        });
    }
            
    useEffect(() => {
        if (
            (
                auth.user?.userdata?.ROLE != "admin" &&
                auth.user?.userdata?.ROLE != "sysadmin" &&
                auth.user?.userdata?.ROLE != "user"
            ) ||
            general.getBreedAccess(auth.user, auth.selectedBreed) == "dogowner"
        ) {
            navigate("/");
        }
    }, [auth.user?.userdata?.ROLE, auth.selectedBreed]);

    useEffect(() => {
        if (params.breedregid != undefined && auth.selectedBreed != undefined && auth.selectedBreed != "") {
            getBreedingDog();
        }
    }, [params.breedregid, auth.selectedBreed]);

    /*
    
    Fields:

    dogName
    dogRegNumber
    otherInfo
    ownerEmail
    ownerName
    ownerPhone
    sickness
    sicknessOtherText
    
    */

    return (
        <div className="maincontent">
            <div className={styles.selectedsicknessreport}>
                <h1>{t("selectedsicknessreport.title")}</h1>
                <hr/>
                
                <button className="btn btn-primary" onClick={() => navigate(listUrl)}>
                    {t("selectedsicknessreport.buttons.back")}
                </button>
                {selectedReg != undefined && selectedReg?.STATUS != "accepted" ?
                    <>
                        <button className="btn btn-primary btn-negative" onClick={() => {
                            rejectBreeding();
                        }}>
                            {t("selectedsicknessreport.buttons.reject")}
                        </button>
                        <button
                            className="btn btn-primary btn-positive"
                            disabled={selectedReg == undefined || selectedReg.DOGMATCH == undefined || selectedReg.DOGMATCH.REGNO == undefined}
                            onClick={() => {
                                if (selectedReg != undefined && selectedReg.DOGMATCH != undefined && selectedReg.DOGMATCH.REGNO != undefined) {
                                    acceptBreeding();
                                }
                            }}
                        >
                            {t("selectedsicknessreport.buttons.accept")}
                        </button>
                    </>
                : null}
                <hr/>
                <Container className={styles.container}>
                    <Row className={styles.row}>
                        <Col
                            sm="12"
                            md="12"
                            lg="6"
                            xl="6"
                            xxl="6"
                            className={styles.col}
                        >
                            <Card
                                title={t("selectedsicknessreport.results.title")}
                                children={<div>
                                    <hr/>
                                    {selectedReg != undefined ?
                                        <table className={styles.generaltable}>
                                            <tbody>
                                                {selectedReg?.DATA?.map((field: any, index: number) => {
                                                    return (
                                                        <tr key={index}>
                                                            <td><strong>{field.TEXT}</strong></td>
                                                            <td>{field.answer}</td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                    :
                                        <div>
                                            {t("selectedsicknessreport.results.loading")}
                                        </div>
                                    }
                                </div>}
                            ></Card>
                        </Col>
                        {selectedReg != undefined ?
                            <Col
                                sm="12"
                                md="12"
                                lg="6"
                                xl="6"
                                xxl="6"
                                className={styles.col}
                            >
                                <Card
                                    title={t("selectedsicknessreport.datamatch.title")}
                                    children={<div>
                                        <hr/>
                                        {selectedReg?.REGNUMBER != undefined ?
                                            <table className={styles.generaltable}>
                                                <tbody>
                                                    <tr>
                                                        <td><strong>{t("selectedsicknessreport.results.fields.dogname")}</strong></td>
                                                        <td>{selectedReg?.DOGMATCH?.NAME}</td>
                                                    </tr>
                                                    <tr>
                                                        <td><strong>{t("selectedsicknessreport.results.fields.regnr")}</strong></td>
                                                        <td>
                                                            <span
                                                                style={{
                                                                    color: "#1D552B",
                                                                    cursor: "pointer",
                                                                    textDecoration: "underline",
                                                                }}
                                                                onClick={() => {
                                                                    // Open in new tab
                                                                    window.open("/hunder/" + selectedReg?.DOGMATCH?.ANIMAL_ID, "_blank");
                                                                }}
                                                            >{selectedReg?.DOGMATCH?.REGNO}</span>
                                                        </td>
                                                    </tr>
                                                    {/* Register what the report shows directly on the matched dog */}
                                                    <HealthStatusRows dogId={selectedReg?.DOGMATCH?.ANIMAL_ID} />
                                                </tbody>
                                            </table>
                                        : null}
                                        {selectedReg?.REGNUMBER != undefined && (
                                            <div style={{ marginTop: '15px' }}>
                                                <button 
                                                    className="btn btn-sm btn-danger"
                                                    onClick={() => {
                                                        if (window.confirm(t("selectedsicknessreport.removeconfirm"))) {
                                                            removeDogMatch();
                                                        }
                                                    }}
                                                >
                                                    {t("selectedsicknessreport.buttons.removematch")}
                                                </button>
                                            </div>
                                        )}
                                        {selectedReg?.REGNUMBER == undefined ?
                                            <div>
                                                <p>{t("selectedsicknessreport.datamatch.nomatch")}</p>
                                                <hr/>
                                                <div>
                                                    <input 
                                                        type="text" 
                                                        className="form-control"
                                                        placeholder={t("selectedsicknessreport.search.placeholder")}
                                                        value={searchTerm}
                                                        onChange={(e) => setSearchTerm(e.target.value)}
                                                        onKeyPress={(e) => {
                                                            if (e.key === 'Enter') {
                                                                searchDog();
                                                            }
                                                        }}
                                                    />
                                                    <button 
                                                        className="btn btn-primary" 
                                                        style={{ marginTop: '10px' }}
                                                        onClick={searchDog}
                                                        disabled={isSearching || !searchTerm}
                                                    >
                                                        {isSearching ? t("selectedsicknessreport.search.searching") : t("selectedsicknessreport.search.button")}
                                                    </button>
                                                </div>
                                                {searchResults.length > 0 && (
                                                    <div style={{ marginTop: '15px' }}>
                                                        {searchResults.length >= 50 && <p style={{ color: 'orange', marginTop: 0, marginBottom: '8px' }}>{t("shared.search.maxresults")}</p>}
                                                        <table className={styles.dogsearchtable} style={{ marginTop: '10px' }}>
                                                            <thead>
                                                                <tr>
                                                                    <th>{t("selectedsicknessreport.results.fields.regnr")}</th>
                                                                    <th>{t("selectedsicknessreport.results.fields.dogname")}</th>
                                                                    <th></th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {searchResults.map((dog: any, index: number) => (
                                                                    <tr key={index}>
                                                                        <td>{dog.REGNO}</td>
                                                                        <td>{dog.NAME}</td>
                                                                        <td>
                                                                            <button 
                                                                                className="btn btn-sm btn-primary"
                                                                                onClick={() => selectDog(dog)}
                                                                            >
                                                                                {t("selectedsicknessreport.search.select")}
                                                                            </button>
                                                                        </td>
                                                                    </tr>
                                                                ))}
                                                            </tbody>
                                                        </table>
                                                        {searchResults.length >= 50 && <p style={{ color: 'orange', marginTop: '8px', marginBottom: 0 }}>{t("shared.search.maxresults")}</p>}
                                                    </div>
                                                )}
                                            </div>
                                        : null}
                                    </div>}
                                ></Card>
                            </Col>
                        : null}
                    </Row>
                </Container>
            </div>
        </div>
    );
};

class SelectedSicknessReportProps {
    
}

export default SelectedSicknessReport;